import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { StarIcon, TrendingUpIcon } from '@heroicons/react/solid'

import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import useModal from '@/hooks/useModalV2'
import Modal from '@/shared/Modal'
import PrimaryButton from '@/shared/Buttons/Primary'
import DefaultButton from '@/shared/Buttons/Default'

// Feature a project (Business plan). Three renders:
//   callout  big full-width upsell card on the dashboard
//   compact  small status-strip badge, only shown once featured
//   default  the old side card, for anywhere else that mounts it
const FeatureModal = ({ compact, callout }) => {
  const { openModal, closeModal, isOpen } = useModal()
  const [, setToast] = useGlobalState('toast')
  const [currentUser] = useGlobalState('currentUser')
  const [manageProjectContainer, setManageProjectContainer] = useGlobalState('manageProjectContainer')
  const { project } = manageProjectContainer
  const { putpostRequest } = useQuery()
  const [loading, setLoading] = useState(false)

  const feature = () => {
    setLoading(true)
    putpostRequest(`/api/v3/manage/projects/${project.id}/feature`, 'POST', {}, (err) => {
      setLoading(false)
      if (err) { return }
      setManageProjectContainer({ ...manageProjectContainer, project: { ...project, featured: true } })
      setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
        <p className='text-sm font-medium text-green-600 dark:text-green-300'>Featured!</p>
        <p className='mt-1 text-sm text-green-500 dark:text-green-300'>Give it up to an hour to float to the top of search.</p>
      </div>)
      closeModal()
    })
  }

  if (project.featured) {
    return compact
      ? <span title='This project sits at the top of search results' className='inline-flex items-center gap-x-1 rounded-full bg-gradient-to-r from-cccpurple via-cccblue to-cccorange px-2.5 py-1 text-xs font-semibold text-white'><StarIcon className='h-3.5 w-3.5' /> Featured</span>
      : <div className='rounded-md border-l-4 border-cccblue bg-cccblue/10 p-2 text-sm text-cccblue'>This project is <b>featured</b>. Give it up to an hour to show at the top of search.</div>
  }

  const trigger = callout
    ? <div className='relative overflow-hidden rounded-2xl bg-gradient-to-r from-cccpurple via-cccblue to-cccorange p-[2px] shadow-md'>
      <div className='flex flex-col gap-4 rounded-2xl bg-white p-4 dark:bg-gray-800 sm:flex-row sm:items-center sm:p-5'>
        <div className='flex-shrink-0 self-start rounded-xl bg-gradient-to-br from-cccpurple via-cccblue to-cccorange p-3 text-white'>
          <StarIcon className='h-8 w-8' />
        </div>
        <div className='min-w-0 flex-1'>
          <h3 className='text-lg font-bold text-gray-900 dark:text-white'>Want more auditions?</h3>
          <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>Featured projects sit above everything else on the search page, which a couple million people scroll every month. They also show up in the sidebar on other projects' pages, so people find you while they're browsing someone else's casting call.</p>
          <div className='mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold text-gray-500 dark:text-gray-400'>
            <span className='inline-flex items-center gap-x-1'><TrendingUpIcon className='h-4 w-4 text-cccorange' /> Top of search</span>
            <span className='inline-flex items-center gap-x-1'><StarIcon className='h-4 w-4 text-cccpurple' /> Promoted on other project pages</span>
          </div>
        </div>
        <button type='button' onClick={openModal}
          className='inline-flex flex-shrink-0 items-center justify-center gap-x-2 rounded-full bg-gradient-to-r from-cccpurple via-cccblue to-cccorange px-6 py-3 text-sm font-bold text-white shadow hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-cccblue focus:ring-offset-2 dark:focus:ring-offset-gray-800'>
          <StarIcon className='h-4 w-4' /> Feature this project
        </button>
      </div>
    </div>
    : compact
    ? <button type='button' onClick={openModal} title='Put this project at the top of search'
      className='inline-flex items-center gap-x-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-800 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-cccblue dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600'>
      <StarIcon className='h-3.5 w-3.5 text-cccorange' /> Feature it
    </button>
    : <div className='w-full rounded-md border-l-4 border-cccblue bg-cccblue/10 p-2'>
      <div className='pb-3 text-sm text-cccblue'><b>Feature</b> your project so that it gets more attention.</div>
      <button type='button' onClick={openModal} className='flex w-full items-center justify-center rounded-md bg-gradient-to-r from-cccpurple via-cccblue to-cccorange px-4 py-2 text-sm font-medium text-white hover:text-white'>Feature</button>
    </div>

  return <>
    {trigger}
    <Modal isOpen={isOpen} closeModal={closeModal}>
      <h2 className='mb-4 text-center text-lg font-bold text-gray-900 dark:text-white'>Put it at the top of search</h2>
      <img src='https://images.castingcall.club/perks/project-featured.jpg' alt='Chart of auditions before and after featuring' className='rounded-lg' />
      <p className='mt-2 text-xs italic text-gray-600 dark:text-gray-300'>A real project from November 2022. About 25 auditions in the 20 days before featuring, then 35 more in the 7 days after.</p>
      <p className='mt-3 text-sm text-gray-700 dark:text-gray-200'>Featured projects sit above everything else in search, which a couple million people scroll through every month. They're also promoted in the sidebar on other projects' pages, so you get found while people browse someone else's casting call.</p>
      { !currentUser.canFeatureProjects && <div className='mt-4 rounded-md bg-yellow-50 p-4 text-sm text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-100'>
        Featuring is a Business plan perk. It also happens to keep my lights on.
        <div className='mt-2'><Link to='/subscriptions' className='inline-flex rounded-md bg-cccblue px-4 py-2 text-sm font-medium text-white hover:bg-cccblue-alt hover:text-white'>Show me the plans</Link></div>
      </div> }
      <div className='mt-4 flex justify-end gap-x-2'>
        <DefaultButton onClick={closeModal} text={currentUser.canFeatureProjects ? 'Not now' : 'Close'} />
        { currentUser.canFeatureProjects && <PrimaryButton onClick={feature} loading={loading} text='Feature it' /> }
      </div>
    </Modal>
  </>
}

export default FeatureModal
